import React from 'react';
import {
  Home,
  BarChart3,
  ShieldAlert,
  Users,
  FileText,
  Receipt,
  RefreshCw,
  ClipboardList,
  Building2,
  CalendarClock,
  Download,
  UserCog,
  KeyRound,
  ScrollText,
  FolderOpen,
  Archive,
  Upload,
  Table2,
  SlidersHorizontal,
} from 'lucide-react';
import MENU_PERMISSIONS from '@/constants/ConstantMenuRoles';

export interface MenuItem {
  label: string;
  path: string;
  icon?: React.ReactNode;
  badge?: string; // ex.: "Nova"
  // Gate de visibilidade: código(s) de permissão exigido(s) (any-of). Ausente = sempre visível.
  // Espelha o campo `permission` de routes.tsx pra o menu não mostrar o que a rota bloqueia.
  permission?: string | string[];
  children?: MenuItem[];
}

const ic = (C: React.ElementType) => <C className="h-5 w-5" />;

// Ordem do top nav do Vue: Trabalhador, Saúde e segurança, Folha de pagamento,
// Encargos, eGov, Certidões (+ Home no topo, Administração no fim via Sidebar).
export const MENU: MenuItem[] = [
  { label: 'Home', path: '/home', icon: ic(Home) },
  {
    label: 'Trabalhador',
    path: '/esocial/trabalhador',
    icon: ic(Users),
    children: [
      {
        label: 'Afastamento e inatividade',
        path: '/esocial/trabalhador/afastamento',
        icon: ic(CalendarClock),
        permission: MENU_PERMISSIONS.AFASTADOS_E_INATIVOS,
      },
      {
        label: 'CBO',
        path: '/esocial/trabalhador/cbo',
        icon: ic(ClipboardList),
        permission: MENU_PERMISSIONS.CBO,
      },
      {
        label: 'Horários e jornadas',
        path: '/esocial/trabalhador/horarios',
        icon: ic(CalendarClock),
        permission: MENU_PERMISSIONS.HORARIOS_E_JORNADA,
      },
    ],
  },
  {
    label: 'Saúde e segurança',
    path: '/saude-seguranca',
    icon: ic(ShieldAlert),
    children: [
      {
        label: 'Agentes nocivos',
        path: '/saude-seguranca/agentes-nocivos',
        icon: ic(ShieldAlert),
        permission: MENU_PERMISSIONS.SAUDE_SEGURANCA_AGENTES_NOCIVOS_APOSENTADORIA_ESPECIAL,
      },
    ],
  },
  {
    label: 'Folha de pagamento',
    path: '/esocial',
    icon: ic(FileText),
    children: [
      {
        label: 'Rubricas',
        path: '/esocial/rubricas',
        icon: ic(ClipboardList),
        permission: MENU_PERMISSIONS.FOLHA_DE_PAGAMENTO,
      },
    ],
  },
  {
    label: 'Encargos',
    path: '/encargos',
    icon: ic(Receipt),
    children: [
      {
        label: 'DCTF',
        path: '/encargos/dctf',
        icon: ic(Receipt),
        permission: MENU_PERMISSIONS.DCTF,
      },
      {
        label: 'Mensagens e-CAC',
        path: '/encargos/mensagens-ecac',
        icon: ic(FileText),
        permission: MENU_PERMISSIONS.MENSAGENS_ECAC,
      },
    ],
  },
  {
    label: 'eGov',
    path: '/egov',
    icon: ic(BarChart3),
    children: [
      {
        label: 'Cessão',
        path: '/egov/cessao',
        icon: ic(Users),
        permission: MENU_PERMISSIONS.CESSAO,
      },
    ],
  },
  {
    label: 'Certidões',
    path: '/certidoes',
    icon: ic(FileText),
    children: [
      {
        label: 'Certidão Federal',
        path: '/certidoes/federal',
        icon: ic(FileText),
        permission: MENU_PERMISSIONS.CERTIDAO_FEDERAL,
      },
      {
        label: 'CRF',
        path: '/certidoes/crf',
        icon: ic(FileText),
        permission: MENU_PERMISSIONS.CRF,
      },
      {
        label: 'CNDT',
        path: '/certidoes/cndt',
        icon: ic(FileText),
        permission: MENU_PERMISSIONS.CNDT,
      },
    ],
  },
  {
    label: 'Arquivos',
    path: '/arquivos',
    icon: ic(Archive),
    children: [
      {
        label: 'Documentos',
        path: '/admin/documentos',
        icon: ic(FolderOpen),
        permission: MENU_PERMISSIONS.CONFIG_DOCUMENTOS_READ,
      },
      { label: 'Exportações', path: '/analytics/downloads', icon: ic(Download) },
    ],
  },
  {
    label: 'eSocial',
    path: '/admin/importacao',
    icon: ic(FileText),
    children: [
      {
        label: 'Importação',
        path: '/admin/importacao',
        icon: ic(Upload),
        permission: [
          MENU_PERMISSIONS.CONFIG_ESOCIAL_IMPORTACAO_READ,
          MENU_PERMISSIONS.CONFIG_ESOCIAL_IMPORTACAO_WRITE,
        ],
      },
      {
        label: 'Central de Reprocessamento',
        path: '/central-de-reprocessamento',
        icon: ic(RefreshCw),
        permission: MENU_PERMISSIONS.CENTRAL_REPROCESSAMENTO,
      },
    ],
  },
];

// Seção "Administração" (Configurações) — renderizada como SECTION (label), com
// subgrupos colapsáveis (Encargos, Empresas, eSocial). A lib suporta 1 nível de grupo por seção.
export const ADMIN_MENU: MenuItem[] = [
  {
    label: 'Auditoria',
    path: '/admin/auditoria',
    icon: ic(ScrollText),
    permission: MENU_PERMISSIONS.CONFIG_AUDITORIA_READ,
  },
  {
    label: 'Empresas',
    path: '/admin/empresas',
    icon: ic(Building2),
    permission: MENU_PERMISSIONS.CONFIG_EMPRESAS_READ,
  },
  {
    label: 'eSocial',
    path: '/admin/tabelas',
    icon: ic(FileText),
    children: [
      {
        label: 'Tabelas',
        path: '/admin/tabelas',
        icon: ic(Table2),
        permission: MENU_PERMISSIONS.CONFIG_TABELAS_HUB,
      },
      {
        label: 'Parâmetros de análise',
        path: '/admin/parametros',
        icon: ic(SlidersHorizontal),
        permission: MENU_PERMISSIONS.CONFIG_PARAMETROS_ANALISE_READ,
      },
    ],
  },
  {
    label: 'Usuários',
    path: '/admin/usuarios',
    icon: ic(UserCog),
    permission: MENU_PERMISSIONS.CONFIG_USUARIOS_READ,
  },
  {
    label: 'Perfis de Permissão',
    path: '/admin/perfis',
    icon: ic(KeyRound),
    permission: MENU_PERMISSIONS.CONFIG_PERFIS_READ,
  },
];
